import React from 'react'
import { ChevronDown, Loader2, Zap, Rocket } from 'lucide-react'

export interface Provider {
  provider: string
  model: string
  available: boolean
  status?: string
}

interface ProviderSelectorProps {
  providers: Provider[]
  currentProvider: Provider | null
  onProviderChange: (provider: Provider) => void
  isLoading?: boolean
}

export const ProviderSelector: React.FC<ProviderSelectorProps> = ({
  providers,
  currentProvider,
  onProviderChange,
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = React.useState(false)

  const handleProviderSelect = (provider: Provider) => {
    if (provider.available) {
      onProviderChange(provider)
      setIsOpen(false)
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        ) : (
          <Zap className="w-4 h-4 text-gray-400" />
        )}
        <span className="text-sm font-medium text-gray-700">
          {currentProvider ? (
            <div className="flex items-center space-x-1">
              <span>{currentProvider.provider} • {currentProvider.model}</span>
              {currentProvider.status?.includes('Cerebras') && (
                <Rocket className="w-3 h-3 text-orange-500" title="Ultra-Fast Cerebras" />
              )}
            </div>
          ) : (
            'Select Provider'
          )}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-20 max-h-80 overflow-y-auto">
            <div className="py-1">
              <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
                Available Providers
              </div>
              
              {providers.length === 0 ? (
                <div className="px-3 py-4 text-sm text-gray-500 text-center">
                  No providers available
                </div>
              ) : (
                providers.map((provider, index) => {
                  const isSelected = currentProvider?.provider === provider.provider && 
                                   currentProvider?.model === provider.model
                  
                  return (
                    <button
                      key={`${provider.provider}-${provider.model}-${index}`}
                      onClick={() => handleProviderSelect(provider)}
                      disabled={!provider.available}
                      className={`w-full px-3 py-3 text-left flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isSelected ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            {provider.provider}
                          </span>
                          {provider.status?.includes('Cerebras') && (
                            <Rocket className="w-3 h-3 text-orange-500" title="Ultra-Fast Cerebras" />
                          )}
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            provider.available 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {provider.available ? 'Available' : 'Unavailable'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Model: {provider.model}
                        </div>
                        {provider.status && (
                          <div className={`text-xs mt-1 ${
                            provider.status.includes('Cerebras') 
                              ? 'text-orange-600 font-medium' 
                              : 'text-gray-400'
                          }`}>
                            {provider.status}
                          </div>
                        )}
                      </div>
                      
                      {isSelected && (
                        <div className="ml-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        </div>
                      )}
                    </button>
                  )
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ProviderSelector